// ─────────────────────────────────────────────────────────────────────────────
// Debug bus — captures browser errors, unhandled promise rejections, console
// output, and API failures into an in-memory ring buffer so the on-screen
// DebugConsole can display "everything" for root-cause analysis when APP_DEBUG
// (VITE_APP_DEBUG) is on. Framework-agnostic; the React UI subscribes to it.
// ─────────────────────────────────────────────────────────────────────────────

export type DebugLevel = 'log' | 'info' | 'warn' | 'error' | 'debug';
export type DebugSource = 'console' | 'window' | 'promise' | 'api' | 'network';

export interface DebugEntry {
  id: number;
  ts: number;
  level: DebugLevel;
  source: DebugSource;
  message: string;
  /** Stack trace or serialized structured detail (JSON). */
  detail?: string;
}

const MAX_ENTRIES = 600;

let entries: DebugEntry[] = [];
let nextId = 1;
let initialized = false;

type Listener = (entries: DebugEntry[]) => void;
const listeners = new Set<Listener>();

function emit(): void {
  const snapshot = entries;
  for (const listener of listeners) listener(snapshot);
}

/** Subscribe to buffer changes. Returns an unsubscribe fn. */
export function subscribeDebug(listener: Listener): () => void {
  listeners.add(listener);
  listener(entries);
  return () => {
    listeners.delete(listener);
  };
}

export function getDebugEntries(): DebugEntry[] {
  return entries;
}

export function clearDebugEntries(): void {
  entries = [];
  emit();
}

/** Safely stringify an arbitrary value (handles circular refs and Errors). */
function safeSerialize(value: unknown): string {
  if (value instanceof Error) {
    return value.stack || `${value.name}: ${value.message}`;
  }
  if (typeof value === 'string') return value;
  const seen = new WeakSet();
  try {
    return JSON.stringify(
      value,
      (_key, val) => {
        if (typeof val === 'object' && val !== null) {
          if (seen.has(val as object)) return '[Circular]';
          seen.add(val as object);
        }
        if (typeof val === 'bigint') return val.toString();
        if (typeof val === 'function') return `[Function ${(val as () => void).name || 'anonymous'}]`;
        return val;
      },
      2
    );
  } catch {
    return String(value);
  }
}

function argsToMessage(args: unknown[]): { message: string; detail?: string } {
  if (args.length === 0) return { message: '' };
  const parts = args.map((a) =>
    typeof a === 'string' ? a : a instanceof Error ? `${a.name}: ${a.message}` : safeSerialize(a)
  );
  const detailParts = args
    .filter((a) => typeof a !== 'string')
    .map((a) => safeSerialize(a));
  return {
    message: parts.join(' '),
    detail: detailParts.length ? detailParts.join('\n\n') : undefined,
  };
}

/** Push a structured entry into the buffer. */
export function pushDebug(
  level: DebugLevel,
  source: DebugSource,
  message: string,
  detail?: string
): void {
  const entry: DebugEntry = { id: nextId++, ts: Date.now(), level, source, message, detail };
  entries = entries.length >= MAX_ENTRIES ? [...entries.slice(1), entry] : [...entries, entry];
  emit();
}

/**
 * Install global capture hooks exactly once. Original console methods are always
 * invoked so the real DevTools console is unaffected — we only tee the output.
 */
export function initDebugBus(): void {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;

  const consoleLevels: DebugLevel[] = ['log', 'info', 'warn', 'error', 'debug'];
  for (const level of consoleLevels) {
    const original = console[level] as (...args: unknown[]) => void;
    console[level] = (...args: unknown[]) => {
      try {
        const { message, detail } = argsToMessage(args);
        pushDebug(level, 'console', message, detail);
      } catch {
        // Never let capture break logging.
      }
      original.apply(console, args as []);
    };
  }

  window.addEventListener('error', (event: ErrorEvent) => {
    const err = event.error as Error | undefined;
    const location = event.filename
      ? `${event.filename}:${event.lineno ?? 0}:${event.colno ?? 0}`
      : undefined;
    pushDebug(
      'error',
      'window',
      err?.message || event.message || 'Uncaught error',
      [err?.stack, location].filter(Boolean).join('\n') || undefined
    );
  });

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const reason = event.reason;
    const message =
      reason instanceof Error
        ? reason.message
        : typeof reason === 'string'
          ? reason
          : 'Unhandled promise rejection';
    pushDebug('error', 'promise', message, safeSerialize(reason));
  });

  pushDebug('info', 'console', 'Debug console initialized — capturing errors, logs & network failures');
}
